/**
 * Nav state for entering builder mode — the chat-plus-object-panel layout for
 * creating and refining one CRM entity conversationally.
 *
 * Serves every automation-kind entity (`ahoy_event_automation`,
 * `ahoy_validation_rule`), which is why this is named for the mode rather than
 * for one entity type.
 *
 * ## What travels, and why so little
 *
 * These helpers target `/:orgId/build` — the window before a conversation
 * exists. The only state that has to survive the hop is the entity type (and,
 * on the edit path, which record), because there is no conversation yet to hold
 * it. It goes in the query string rather than route state so a reload or a
 * shared link still works.
 *
 * The moment the user sends, `useStartNewConversation` creates a conversation
 * carrying `builder_entity_type`/`builder_entity_id` server-side, and every
 * later navigation reads the mode from the conversation. So this is the only
 * place builder state lives in a URL, and it stops mattering after one message.
 */

import {
  BUILDER_ENTITY_ID_PARAM,
  BUILDER_ENTITY_TYPE_PARAM,
  builderRoutePath,
} from '@/lib/crm/hooks/useBuilderMode';

export interface BuilderModeNavState {
  /** Full path including search params — pass straight to `navigate(to, { state })`. */
  to: string;
  state: {
    initialPrompt: string;
    fromConversationsHome: true;
    /** CRM entity ids to ground the conversation on open; omitted when empty. */
    entityIds?: string[];
  };
}

function buildSearch(entityTypeName: string, entityId?: string): string {
  const params = new URLSearchParams();
  params.set(BUILDER_ENTITY_TYPE_PARAM, entityTypeName);
  if (entityId) {
    params.set(BUILDER_ENTITY_ID_PARAM, entityId);
  }
  return `?${params.toString()}`;
}

/**
 * Enter builder mode to create a NEW entity of the given type.
 *
 * No entity exists yet — and none will until the agent's proposal is approved,
 * since the create actions write nothing before `execute()`. The panel opens on
 * the type's shape and fills in as the agent proposes.
 *
 * Pass `entityIds` to ground the conversation on specific existing records; the
 * ids ride in nav state and are threaded into the new conversation by the composer.
 */
export function buildCreateInBuilderNavState(
  orgId: number,
  entityTypeName: string,
  initialPrompt: string,
  entityIds?: string[]
): BuilderModeNavState {
  return {
    to: `${builderRoutePath(orgId)}${buildSearch(entityTypeName)}`,
    state: {
      initialPrompt,
      fromConversationsHome: true,
      ...(entityIds && entityIds.length > 0 ? { entityIds } : {}),
    },
  };
}

/**
 * Enter builder mode to edit an EXISTING entity — the panel opens on it
 * immediately.
 *
 * The prompt deliberately carries NO `<Entity>` mention of the record. The user
 * should not have to reference the object they are visibly looking at: the open
 * panel IS the context, and the conversation itself records which record it is
 * about, so the agent is grounded regardless of what the user types.
 */
export function buildEditInBuilderNavState(
  orgId: number,
  entityTypeName: string,
  entityId: string,
  initialPrompt: string
): BuilderModeNavState {
  return {
    to: `${builderRoutePath(orgId)}${buildSearch(entityTypeName, entityId)}`,
    state: {
      initialPrompt,
      fromConversationsHome: true,
    },
  };
}
